/** @jsxImportSource @emotion/react */
import { useTheme } from '@mui/material/styles'
import { Badge } from '@app/components/Atoms/Badge'
import { Box } from '@mui/material'
import { appLink } from '@app/config/appLink'
import { SidebarAtom } from '@app/state/layoutAtom'
import { isItemActive } from '@app/utils/isItemActive'
import { motion } from 'framer-motion'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { sidebarVariants } from './Sidebar'
import NewSidebarMenuItem from './SidebarMenuItem'
import { getVisibleMenuItems } from '@app/utils/getVisibleMenuItems'
import { featureFlagMapAtom } from '@app/state/featureFlagAtoms'
import { sidebarFullyLoadedAtom, sidebarMenuLoadedAtom } from '@app/state/sidebarAtom'
import { sidebarButtonsContainer, sidebarButtonsInnerContainer } from './SidebarMenu.style'
import { SidebarSkeleton } from './Sidebar.skeleton'
import { isAuthenticatedAtom } from '@app/state/authAtom'

export const SidebarMenu = () => {
  const location = useLocation()

  const [isOpen] = useAtom(SidebarAtom)
  const theme = useTheme()
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const featureFlagMap = useAtomValue(featureFlagMapAtom)
  const sidebarFullyLoaded = useAtomValue(sidebarFullyLoadedAtom)
  const setSidebarMenuLoaded = useSetAtom(sidebarMenuLoadedAtom)

  const visibleMenuItems = useMemo(() => {
    const visibleItem = getVisibleMenuItems(appLink, isAuthenticated, featureFlagMap)
    setSidebarMenuLoaded(true)
    return visibleItem
  }, [isAuthenticated, featureFlagMap, setSidebarMenuLoaded])

  return (
    <motion.div
      variants={sidebarVariants}
      animate={isOpen ? 'open' : 'closed'}
      transition={{ duration: 0.2 }}
      css={sidebarButtonsContainer(theme)}
    >
      <SidebarSkeleton loading={!sidebarFullyLoaded} rows={6} rowHeight={49} />
      <Box css={sidebarButtonsInnerContainer(theme, sidebarFullyLoaded)}>
        {sidebarFullyLoaded &&
          visibleMenuItems.map((item) => (
            <NewSidebarMenuItem
              key={item.icon}
              icon={item.icon}
              label={item.label}
              active={isItemActive(item.path, location.pathname)}
              onClick={() => {
                if (item.absolutePath) {
                  window.open(item.path, '_blank')
                } else {
                  navigate(item.path)
                }
              }}
              disabled={false}
              hidden={false}
              displayLabel={isOpen}
              badge={item.isBeta && <Badge>Beta</Badge>}
              size="large"
            />
          ))}
      </Box>
    </motion.div>
  )
}
